// Calendário completo (pedido 2026-10-21: "abrir o calendário completo,
// escolher o ano"): grade do mês (seg–dom), setas, toque no mês/ano abre a
// escolha de ano e de mês. Datas como "YYYY-MM-DD" (sem fuso no meio).
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const WEEK = ["S", "T", "Q", "Q", "S", "S", "D"];
const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const MONTHS_LONG = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const pad = (n: number) => String(n).padStart(2, "0");
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

export function CalendarPicker({
  value,
  onChange,
  today,
  marked,
  onVisibleMonthChange,
}: {
  value: string;
  onChange: (ymd: string) => void;
  today: string;
  /** dias com compromisso (bolinha) */
  marked?: Set<string>;
  onVisibleMonthChange?: (ym: string) => void;
}) {
  const [view, setView] = useState(() => ({ y: Number(value.slice(0, 4)), m: Number(value.slice(5, 7)) }));
  const [mode, setMode] = useState<"days" | "months" | "years">("days");

  const go = (y: number, m: number) => {
    const d = new Date(Date.UTC(y, m - 1, 1));
    const next = { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1 };
    setView(next);
    onVisibleMonthChange?.(`${next.y}-${pad(next.m)}`);
  };

  const first = new Date(Date.UTC(view.y, view.m - 1, 1)).getUTCDay(); // 0 = domingo
  const lead = first === 0 ? 6 : first - 1;
  const total = daysIn(view.y, view.m);
  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: total }, (_, i) => `${view.y}-${pad(view.m)}-${pad(i + 1)}`),
  ];
  const thisYear = Number(today.slice(0, 4));
  const years = Array.from({ length: 16 }, (_, i) => thisYear - 5 + i);

  return (
    <div className="select-none">
      <div className="mb-2 flex items-center gap-1">
        <button
          type="button"
          onClick={() => setMode(mode === "days" ? "years" : "days")}
          className="flex-1 rounded-xl px-2 py-1.5 text-left text-base font-extrabold text-slate-900 hover:bg-slate-50 dark:text-white dark:hover:bg-slate-800"
        >
          {MONTHS_LONG[view.m - 1]} {view.y} <span className="ai-text">▾</span>
        </button>
        {mode === "days" && (
          <>
            <button type="button" aria-label="Mês anterior" onClick={() => go(view.y, view.m - 1)} className="grid h-9 w-9 place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
              <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-300" />
            </button>
            <button type="button" aria-label="Próximo mês" onClick={() => go(view.y, view.m + 1)} className="grid h-9 w-9 place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
              <ChevronRight className="h-5 w-5 text-slate-600 dark:text-slate-300" />
            </button>
          </>
        )}
      </div>

      {mode === "years" && (
        <div className="grid grid-cols-4 gap-2 py-1">
          {years.map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => {
                go(y, view.m);
                setMode("months");
              }}
              className={cn(
                "rounded-2xl py-2.5 text-sm font-bold transition active:scale-95",
                y === view.y ? "ai-grad shadow-md" : "bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
                y === thisYear && y !== view.y && "ring-2 ring-inset ai-border",
              )}
            >
              {y}
            </button>
          ))}
        </div>
      )}

      {mode === "months" && (
        <div className="grid grid-cols-4 gap-2 py-1">
          {MONTHS.map((label, i) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                go(view.y, i + 1);
                setMode("days");
              }}
              className={cn(
                "rounded-2xl py-3 text-sm font-bold transition active:scale-95",
                i + 1 === view.m ? "ai-grad shadow-md" : "bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {mode === "days" && (
        <>
          <div className="grid grid-cols-7 text-center text-[11px] font-bold uppercase text-slate-400">
            {WEEK.map((d, i) => (
              <span key={i} className="py-1">
                {d}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-y-1">
            {cells.map((d, i) =>
              d ? (
                <button
                  key={d}
                  type="button"
                  onClick={() => onChange(d)}
                  aria-pressed={d === value}
                  className={cn(
                    "mx-auto flex h-10 w-10 flex-col items-center justify-center rounded-full text-sm font-semibold transition active:scale-90",
                    d === value
                      ? "ai-grad shadow-md"
                      : d === today
                        ? "ai-text ring-2 ring-inset ai-border"
                        : d < today
                          ? "text-slate-400"
                          : "text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800",
                  )}
                >
                  {Number(d.slice(8))}
                  {marked?.has(d) && (
                    <i className={cn("mt-0.5 block h-1 w-1 rounded-full", d === value ? "bg-white" : "bg-[var(--ai)]")} />
                  )}
                </button>
              ) : (
                <span key={`x${i}`} />
              ),
            )}
          </div>
        </>
      )}
    </div>
  );
}
